// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import FadeInView from '@/components/common/FadeInView';
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';

// ** Types
import type { Theme } from '@mui/material/styles';
import type { AppContext } from '@/types/contextTypes';
import type { Maybe, UploadFile } from '@/types/graphql';

export type ImageelementProps = {
	host: AppContext['host'];
	imageData: UploadFile;
	/** Optional title shown below the image (left column) */
	title?: string;
	/** Optional description shown below the image (right column) */
	description?: string;
	caption?: Maybe<string>;
};

const ImageElement = ({ host, imageData, title, description, caption }: ImageelementProps) => {
	const theme = useTheme();

	const TWO_COL_MEDIA = `@media (min-width: ${(theme as Theme & { layout: { twoColMinWidthPx: number } }).layout.twoColMinWidthPx}px)`;
	const hasTextSection = Boolean(title || description);
	const imageCaption = caption ?? imageData.caption;

	return (
		<FadeInView>
			<Box sx={{ py: { xs: 8, md: 12 } }}>
				{/* Image container */}
				<Box
					sx={{
						position: 'relative',
						borderRadius: '0.5rem',
						overflow: 'hidden',
						width: '100%',
						aspectRatio: '16 / 9'
					}}
				>
					<StrapiImageRenderer
						imageData={imageData}
						host={host}
						fill
						// alt={imageAlt}
						sizes="1080px"
						imageFit="cover"
					/>
				</Box>

				{/* Caption */}
				{imageCaption && (
					<Typography
						variant="caption"
						component="figcaption"
						sx={{
							display: 'block',
							position: 'absolute',
							mt: 1,
							color: 'text.secondary',
							fontStyle: 'italic'
						}}
					>
						{imageCaption}
					</Typography>
				)}

				{/* Text section */}
				{hasTextSection && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '1rem',
							mt: { xs: '1.5rem', md: '2rem' },
							[TWO_COL_MEDIA]: {
								flexDirection: 'row',
								gap: '5.5rem'
							}
						}}
					>
						{title && (
							<Box
								sx={{
									minWidth: 0,
									[TWO_COL_MEDIA]: { flex: '1 1 0' }
								}}
							>
								<Typography
									variant="title-md"
									sx={(t) => ({
										fontSize: '1.5rem',
										lineHeight: '2rem',
										letterSpacing: '-0.03em',
										color: 'text.primary',
										[t.breakpoints.up('md')]: {
											fontSize: '2rem',
											lineHeight: '2.5rem',
											letterSpacing: '-0.03em'
										},
										[t.breakpoints.up('lg')]: {
											fontSize: '2rem',
											lineHeight: '2.5rem',
											letterSpacing: '-0.03em'
										}
									})}
								>
									{title}
								</Typography>
							</Box>
						)}

						{description && (
							<Box
								sx={{
									minWidth: 0,
									[TWO_COL_MEDIA]: { flex: '1 1 0' }
								}}
							>
								<Typography variant="body-sm" sx={{ color: 'text.secondary' }}>
									{description}
								</Typography>
							</Box>
						)}
					</Box>
				)}
			</Box>
		</FadeInView>
	);
};

export default ImageElement;
